import {
  findBasePlanId,
  readLagoCredentials,
  resolveProviderAssignment,
  roundAddonsPayload,
  roundCardAcceptPayload,
  roundCardInitFields,
  roundPaypalAcceptPayload,
  roundPaypalInitPayload,
} from '../shared/utils/roundPayments';
import { PAYMENT_REQUEST_TIMEOUT_MS } from '../common/constants/paymentInit';

const TIMED_REQUEST = { timeout: PAYMENT_REQUEST_TIMEOUT_MS };

type ApiClient = { post: (url: string, body?: any, options?: Record<string, unknown>) => Promise<any> };

export function useRoundKegelApi(client: ApiClient) {
  const {
    PAYPAL_GATEWAY_ID,
    BUSINESS_ENTITY_ID,
  } = useRuntimeConfig().public;
  const { locale } = useI18n();
  const store = useKegelAnalyticStore();
  const gatewayStore = useKegelGatewayStore();
  const configsStore = usePaymentConfigs(locale);
  const route = useRoute();

  const isRound = computed(() => gatewayStore.provider === 'round');

  function gateway() {
    return {
      paypalGatewayId: gatewayStore.pgid || PAYPAL_GATEWAY_ID,
      businessEntityId: gatewayStore.beid || BUSINESS_ENTITY_ID,
    };
  }

  function currencyCode(): string {
    return store.subscriptionInfo?.currencyCode
      ?? configsStore.currentConfig[1]?.currencyCode
      ?? 'USD';
  }

  function applyLagoCredentials(res: any): void {
    const { lagoPaypalClientId, lagoHyperswitchPk, lagoCardProvider } = readLagoCredentials(res);
    if (lagoPaypalClientId) gatewayStore.lagoPaypalClientId = lagoPaypalClientId;
    if (lagoHyperswitchPk) gatewayStore.lagoHyperswitchPk = lagoHyperswitchPk;
    if (lagoCardProvider) gatewayStore.lagoCardProvider = lagoCardProvider;
  }

  function applyProvider(res: any): void {
    gatewayStore.providerResolved = true;
    if (gatewayStore.railLocked) return;

    const requested = usePaymentProviderOverride() || res?.subscription_service || res?.billing_provider;
    const assignment = resolveProviderAssignment(requested, !!useAppConfig().chargebeeOnly);
    if (assignment) Object.assign(gatewayStore, assignment);
  }

  /** Every payment request echoes back the rail the backend assigned. */
  function withSubscriptionService(payload: Record<string, unknown>): Record<string, unknown> {
    return { ...payload, subscription_service: gatewayStore.subscriptionService };
  }

  async function paypalInit(uuid: string, amount?: number): Promise<any> {
    return await client.post('/web-payment/init/pay-pal-payment/', withSubscriptionService(roundPaypalInitPayload(
      uuid,
      gateway(),
      currencyCode(),
      amount,
    )), TIMED_REQUEST);
  }

  async function payPalAccept(payload: unknown): Promise<any> {
    return await client.post('/web-payment/accept/pay-pal-payment', withSubscriptionService(roundPaypalAcceptPayload(
      payload as Record<string, unknown>,
      store.userUuid,
      gateway(),
    )));
  }

  function cardInitFields(name?: string): Record<string, unknown> {
    return withSubscriptionService(roundCardInitFields(store.userEmail, name));
  }

  async function creditCardAccept(payload: unknown): Promise<any> {
    return await client.post('/web-payment/accept/card-payment/', withSubscriptionService(roundCardAcceptPayload(
      payload as Record<string, unknown>,
      store.userUuid,
    )), TIMED_REQUEST);
  }

  async function buyAddons(payload: unknown): Promise<any> {
    return await client.post('/web-payment/addons/', withSubscriptionService(roundAddonsPayload(
      payload as Record<string, unknown> & { item?: string },
      findBasePlanId(configsStore.currentConfig, route.query.pID),
    )), TIMED_REQUEST);
  }

  return {
    isRound,
    currencyCode,
    applyLagoCredentials,
    applyProvider,
    paypalInit,
    payPalAccept,
    cardInitFields,
    creditCardAccept,
    buyAddons,
  };
}
